import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { Link as RouterLink } from 'react-router-dom';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Tooltip from '@mui/material/Tooltip';
import { API_URL } from '../../api';
import YearGroupSelect from '../../components/YearGroupSelect';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const STATUSES = [
    { value: '', label: 'Any status' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
];
const STATUS_COLORS = { draft: 'default', published: 'success', archived: 'warning' };

const dayLabel = (n) => WEEKDAYS[n] || '—';

export default function TutoringClassesPage() {
    const confirm = useConfirm();
    const [items, setItems] = useState([]);
    const [years, setYears] = useState([]);
    const [terms, setTerms] = useState([]);
    const [tutors, setTutors] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [academicYearId, setAcademicYearId] = useState('');
    const [termId, setTermId] = useState('');
    const [tutorId, setTutorId] = useState('');
    const [yearGroup, setYearGroup] = useState('');
    const [subject, setSubject] = useState('');
    const [status, setStatus] = useState('');
    const [dayOfWeek, setDayOfWeek] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (academicYearId) params.set('academicYearId', academicYearId);
            if (termId) params.set('termId', termId);
            if (tutorId) params.set('tutorId', tutorId);
            if (yearGroup) params.set('yearGroup', yearGroup);
            if (subject) params.set('subject', subject);
            if (status) params.set('status', status);
            if (dayOfWeek !== '') params.set('dayOfWeek', dayOfWeek);
            const { data } = await axios.get(
                `${API_URL}/api/admin/tutoring-classes?${params.toString()}`,
                authHeaders()
            );
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setError('Failed to load.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial reference data
        axios.get(`${API_URL}/api/admin/academic-years`, authHeaders())
            .then((r) => {
                setYears(r.data || []);
                const active = (r.data || []).find((y) => y.isActive);
                if (active) setAcademicYearId(active._id);
            })
            .catch(() => {});
        axios.get(`${API_URL}/api/admin/accounts?role=teacher&limit=200`, authHeaders())
            .then((r) => setTutors(r.data?.items || []))
            .catch(() => setTutors([]));
        axios.get(`${API_URL}/api/subjects?activeOnly=true`)
            .then((r) => setSubjects(Array.isArray(r.data) ? r.data.map((s) => s.name).filter(Boolean) : []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!academicYearId) { setTerms([]); return; }
        axios
            .get(`${API_URL}/api/admin/terms?academicYearId=${academicYearId}`, authHeaders())
            .then((r) => setTerms(r.data || []))
            .catch(() => setTerms([]));
    }, [academicYearId]);

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [academicYearId, termId, tutorId, yearGroup, subject, status, dayOfWeek]);

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete class', message: 'Delete this class? Prefer Archive if you\'ve run lessons against it — archive preserves the history.', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/admin/tutoring-classes/${id}`, authHeaders());
            setMessage('Class deleted.');
            await load();
        } catch {
            setError('Failed to delete.');
        }
    };

    const publish = async (id) => {
        try {
            await axios.post(`${API_URL}/api/admin/tutoring-classes/${id}/publish`, {}, authHeaders());
            setMessage('Class published — announcements queued.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to publish.');
        }
    };

    const archive = async (id) => {
        if (!(await confirm({ title: 'Archive class', message: 'Roster members will fall back to "not enrolled" if they aren\'t on any other live class.', confirmLabel: 'Archive' }))) return;
        try {
            await axios.post(`${API_URL}/api/admin/tutoring-classes/${id}/archive`, {}, authHeaders());
            setMessage('Class archived.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to archive.');
        }
    };

    const unarchive = async (id) => {
        try {
            await axios.post(`${API_URL}/api/admin/tutoring-classes/${id}/unarchive`, {}, authHeaders());
            setMessage('Class moved back to draft. Publish it again to make it live.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to restore.');
        }
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Tutoring classes</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                        Weekly teaching groups. A class is a tutor + students meeting every week
                        during one or more terms. Publishing fires the announcement email to
                        everyone in the group.
                    </Typography>
                    
                </Box>
                <Button
                    component={RouterLink}
                    to="/tutoring-classes/new"
                    variant="contained"
                    sx={{ padding: '8px 22px', fontFamily: '"Inter"', fontWeight: 500, color: '#fff !important' }}
                >
                    New class
                </Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Click <strong>New class</strong> to draft a weekly teaching group — pick the tutor, the students, the day and time, the term(s) it runs across.<br />
                            <strong>2.</strong> Save it as a draft while you finish the roster. Nothing is emailed yet.<br />
                            <strong>3.</strong> When ready, click <strong>Publish</strong>. This generates a lesson row for every date the class runs (skipping term-break blocked dates) and sends the announcement email to the tutor and every parent/student on the roster.<br />
                            <strong>4.</strong> Use the row icons to <strong>Edit</strong>, <strong>Archive</strong> (icon-only, preserves history), or <strong>Delete</strong> (last resort).<br />
                            <strong>5.</strong> Archived classes disappear from the default filters but keep their full history — attendance, cover requests, progress reports all stay attached.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        select size="small" label="Academic year"
                        value={academicYearId} onChange={(e) => { setAcademicYearId(e.target.value); setTermId(''); }}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {years.map((y) => (
                            <MenuItem key={y._id} value={y._id}>{y.label}{y.isActive ? ' (active)' : ''}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select size="small" label="Term"
                        value={termId} onChange={(e) => setTermId(e.target.value)}
                        sx={{ minWidth: 140 }} disabled={!academicYearId}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {terms.map((t) => (
                            <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select size="small" label="Tutor"
                        value={tutorId} onChange={(e) => setTutorId(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {tutors.map((t) => (
                            <MenuItem key={t._id} value={t._id}>{t.firstName} {t.surname}</MenuItem>
                        ))}
                    </TextField>
                    <YearGroupSelect value={yearGroup} onChange={setYearGroup} sx={{ minWidth: 160 }} />
                    <TextField
                        select size="small" label="Subject"
                        value={subject} onChange={(e) => setSubject(e.target.value)}
                        sx={{ minWidth: 180 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {subjects.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select size="small" label="Day"
                        value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}
                        sx={{ minWidth: 140 }}
                    >
                        <MenuItem value="">Any</MenuItem>
                        {WEEKDAYS.map((d, i) => (
                            <MenuItem key={i} value={i}>{d}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select size="small" label="Status"
                        value={status} onChange={(e) => setStatus(e.target.value)}
                        sx={{ minWidth: 140 }}
                    >
                        {STATUSES.map((s) => (
                            <MenuItem key={s.value || 'any'} value={s.value}>{s.label}</MenuItem>
                        ))}
                    </TextField>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Tutor</TableCell>
                                <TableCell>Students</TableCell>
                                <TableCell>When</TableCell>
                                <TableCell>Year / Subject</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((c) => (
                                <TableRow key={c._id}>
                                    <TableCell>{c.name}</TableCell>
                                    <TableCell>
                                        {c.tutorId ? `${c.tutorId.firstName || ''} ${c.tutorId.surname || ''}`.trim() : '—'}
                                    </TableCell>
                                    <TableCell>{(c.studentIds || []).length}</TableCell>
                                    <TableCell>{dayLabel(c.dayOfWeek)} {c.startsAt}</TableCell>
                                    <TableCell>
                                        {[c.yearGroup, c.subject].filter(Boolean).join(' · ') || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={c.status} color={STATUS_COLORS[c.status] || 'default'} />
                                    </TableCell>
                                    {/* Row actions:
                                          Publish → primary (main CTA)
                                          Edit    → secondary (pink)
                                          Archive → icon-only (like Delete)
                                          Delete  → icon-only, red on hover */}
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                                            {c.status === 'draft' && (
                                                <Button size="small" variant="primary" onClick={() => publish(c._id)}>
                                                    Publish
                                                </Button>
                                            )}
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    component={RouterLink}
                                                    to={`/tutoring-classes/${c._id}`}
                                                    size="small"
                                                    aria-label="Edit class"
                                                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {(c.status === 'draft' || c.status === 'published') && (
                                                <Tooltip title="Archive">
                                                    <IconButton
                                                        onClick={() => archive(c._id)}
                                                        size="small"
                                                        aria-label="Archive class"
                                                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                    >
                                                        <Inventory2OutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {c.status === 'archived' && (
                                                <Tooltip title="Restore to draft">
                                                    <IconButton
                                                        onClick={() => unarchive(c._id)}
                                                        size="small"
                                                        aria-label="Restore class to draft"
                                                        sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                                    >
                                                        <UnarchiveOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    onClick={() => remove(c._id)}
                                                    size="small"
                                                    aria-label="Delete class"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            color: 'error.main',
                                                            bgcolor: 'rgba(220, 38, 38, 0.08)',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No classes match the current filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Container>
    );
}